/**
 * Association names for SORTING and LIST display.
 *
 * A dozen communities are named "Courtyards at/of/on …" (or bare "Courtyards …"), which
 * clumps them under C and hides the part people actually search by. Lists sort by the
 * unique part and show it with a Courtyards badge; legal surfaces (print, letters, the hub
 * header) keep the full `association.name`.
 */

const LEADING_THE = /^the\s+/i;
const COURTYARDS_PREFIX = /^courtyards(?:\s+(?:at|of|on))?\s+/i;

const withoutThe = (name: string): string => name.replace(LEADING_THE, '');

/** True for "Courtyards at|of|on …" and bare "Courtyards …", with or without a leading "The". */
export const isCourtyards = (name: string | null | undefined): boolean =>
	COURTYARDS_PREFIX.test(withoutThe((name ?? '').trim()));

/** The name without its Courtyards prefix; unchanged when there is none (or nothing would remain). */
export const associationShortName = (name: string | null | undefined): string => {
	const trimmed = (name ?? '').trim();
	if (!isCourtyards(trimmed)) return trimmed;
	return withoutThe(trimmed).replace(COURTYARDS_PREFIX, '') || trimmed;
};

/** Lower-cased sort key: Courtyards prefix and a leading "The" removed. */
export const sortableAssociationName = (name: string | null | undefined): string =>
	withoutThe(associationShortName(name)).toLowerCase();

/** Comparator for association names; ties broken by the full name so the order is total. */
export const compareAssociationNames = (a: string | null | undefined, b: string | null | undefined): number =>
	sortableAssociationName(a).localeCompare(sortableAssociationName(b)) || (a ?? '').localeCompare(b ?? '');
